import {getUrlForGeneratedAiChatModelAssetsImage} from '@Libraries/ai-chat.library';
import {CHAT_MEDIA_FOLDERS, getMediaDistributionHosts, MediaItemSizeType} from '@Libraries/s3-library';
import {isSocialAccountAvatarHost} from '@Libraries/social-media-platform.library';
import {parsePlainHttpsUrl} from '@Utils/url.util';

/** A model-asset hashedId, the only bare media reference expanded into a URL. */
const CHAT_MEDIA_HASHED_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

/** A '.' or '..' segment, plain or percent-escaped, which a URL parser resolves away. */
const DOT_SEGMENT = String.raw`(?:\.|%2e){1,2}(?:/|$)`;

const UNRESERVED_CHAR = '[A-Za-z0-9._~-]';

/** Any percent-escape except a control byte, '/', '\' or DEL. */
const SAFE_PERCENT_ESCAPE = '%(?![01]|2f|5c|7f)[0-9a-f]{2}';

const PATH_SEGMENT = `(?!${DOT_SEGMENT})(?:${UNRESERVED_CHAR}|${SAFE_PERCENT_ESCAPE})+`;

const MEDIA_FILE_PATH_PATTERN = new RegExp(`^/(?:${PATH_SEGMENT}/)*${PATH_SEGMENT}$`, 'i');

const SITE_ASSET_PATH_PATTERN = new RegExp(`^(?:${PATH_SEGMENT}/)*${PATH_SEGMENT}$`, 'i');

const MEDIA_CACHE_BUSTER_PATTERN = /^\?ts=\d+$/;

/** A backslash or a '.' / '..' segment (plain or escaped), which the URL parser would resolve away before the path is checked. */
const UNSAFE_RAW_MEDIA_URL_PATTERN = /\\|(?:^|\/)(?:\.|%2e){1,2}(?:[/?#]|$)/i;

/** A bare AI-chat asset hashedId becomes its asset URL; a full URL is kept only when it names a file on our media distributions. Null means render no image. */
export const resolveChatMediaUrl = (value: string | undefined, sizeType: MediaItemSizeType): string | null => {
  if (value === undefined || value === '') {
    return null;
  }

  if (CHAT_MEDIA_HASHED_ID_PATTERN.test(value)) {
    return getUrlForGeneratedAiChatModelAssetsImage(value, sizeType);
  }

  return getOurMediaUrl(value);
};

/** {@link resolveChatMediaUrl} for a survey option's picture, which may also be a connected account's avatar on the platform's own host or our default avatar. */
export const resolveChatOptionImageUrl = (value: string): string | null => {
  const ourMediaUrl = resolveChatMediaUrl(value, MediaItemSizeType.THUMB);
  if (ourMediaUrl !== null) {
    return ourMediaUrl;
  }

  return isSiteAssetUrl(value) ? value : getSocialAccountAvatarUrl(value);
};

/** A URL naming a file on our media distributions, normalized as the browser requests it; never a bare hashedId. */
export const getOurMediaUrl = (value: string): string | null => {
  const mediaUrl = parsePlainHttpsUrl(value);
  if (mediaUrl === null || UNSAFE_RAW_MEDIA_URL_PATTERN.test(value) || !getMediaDistributionHosts().includes(mediaUrl.hostname)) {
    return null;
  }

  return isChatMediaPath(mediaUrl) ? mediaUrl.href : null;
};

/** The distributions also proxy webapp routes, so the folder is pinned and the query limited to our cache-buster, as on the server. */
const isChatMediaPath = ({pathname, search, hash}: URL): boolean => {
  const isOurQuery = search === '' || MEDIA_CACHE_BUSTER_PATTERN.test(search);

  return hash === '' && isOurQuery && isInChatMediaFolder(pathname) && MEDIA_FILE_PATH_PATTERN.test(pathname);
};

const isInChatMediaFolder = (pathname: string): boolean => {
  return CHAT_MEDIA_FOLDERS.some((folder) => pathname.startsWith(`/${folder}/`));
};

const getSocialAccountAvatarUrl = (value: string): string | null => {
  const avatarUrl = parsePlainHttpsUrl(value);

  return avatarUrl !== null && isSocialAccountAvatarHost(avatarUrl.hostname) ? avatarUrl.href : null;
};

/** A file under the site's own assets folder, on this page's origin (the default profile picture). */
const isSiteAssetUrl = (value: string): boolean => {
  const assetsRoot = window.PMW.util.asset_url('') ?? '';

  return (
    assetsRoot.startsWith(`${window.location.origin}/`) &&
    value.startsWith(assetsRoot) &&
    !UNSAFE_RAW_MEDIA_URL_PATTERN.test(value) &&
    SITE_ASSET_PATH_PATTERN.test(value.slice(assetsRoot.length))
  );
};
